import { tracker } from '@lessonup/analytics';
import {
  NotificationModal,
  NotificationModalParams,
  NotificationModalResponse,
  ChannelLeadModal,
  ChannelLeadConfig,
  ChannelLeadResponse,
  ConfirmModal,
  ConfirmModalParams,
  CookiePopup,
  FollowChannelModal,
  FollowChannelModalConfig,
  PromptStringModal,
  PromptStringModalParams,
  PromptStringModalResponse,
  SchoolSuggestion,
  SchoolSuggestionModalProps,
  SchoolSuggestionModalResponse,
  ProRequiredModal,
  ProRequiredModalProps,
  ProRequiredModalResponse,
  AppModalManagerService,
} from '@lessonup/typescript-core/ui';
import { assertNever } from '@lessonup/typescript-core/utils';
import React from 'react';
// eslint-disable-next-line import/no-cycle -- auto-generated TECH-258
import LoginModal, { LoginModalConfig } from '../components/login/LoginModal';
import {
  LocationPickerModalProvider,
  LocationPickerModalProviderParams,
  LocationPickerModalProviderResponse,
} from '../components/modals';
import FlagLessonModal, { FlagLessonModalConfig } from '../components/modals/FlagLessonModal/FlagLessonModal';
import { FollowChannelModalResponse } from '../components/modals/FollowChannelModal/FollowChannelModal';
import ForgotPasswordModal from '../components/modals/ForgotPasswordModal/ForgotPasswordModal';
import { LessonsSyncedModal } from '../components/modals/LessonsSyncedModal';
import LinkModal, { LinkModalConfig } from '../components/modals/LinkModal/LinkModal';
import LockedFolderModal, { LockedFolderModalProps } from '../components/modals/LockedFolderModal/LockedFolderModal';
import LoginPromptModal, {
  LoginPromptConfig,
  LoginPromptModalResult,
} from '../components/modals/LoginPromptModal/LoginPromptModal';
// eslint-disable-next-line import/no-named-as-default -- auto-generated TECH-258
import RegisterOrLoginModal, {
  RegisterOrLoginModalConfig,
} from '../components/modals/RegisterOrLoginModal/RegisterOrLoginModal';
import ShareLessonModal, { ShareLessonModalConfig } from '../components/modals/ShareLessonModal/ShareLessonModal';

type ModalsWithConfig =
  | 'login'
  | 'confirm'
  | 'locationPicker'
  | 'promptString'
  | 'shareLesson'
  | 'requestFollowChannel'
  | 'flagLesson'
  | 'link'
  | 'lockedFolder'
  | 'proRequired'
  | 'channelLead'
  | 'loginPrompt'
  | 'notification'
  | 'schoolSuggestions'
  | 'registerOrLogin';

type ModalsWithoutConfig = 'lessonSynced' | 'forgotPassword' | 'cookiePreferences';
type AllModals = ModalsWithConfig | ModalsWithoutConfig;

type ModalOptions<T extends AllModals> = T extends 'login'
  ? LoginModalConfig
  : T extends 'confirm'
    ? ConfirmModalParams
    : T extends 'locationPicker'
      ? LocationPickerModalProviderParams
      : T extends 'promptString'
        ? PromptStringModalParams
        : T extends 'shareLesson'
          ? ShareLessonModalConfig
          : T extends 'flagLesson'
            ? FlagLessonModalConfig
            : T extends 'requestFollowChannel'
              ? FollowChannelModalConfig
              : T extends 'link'
                ? LinkModalConfig
                : T extends 'lockedFolder'
                  ? LockedFolderModalProps
                  : T extends 'proRequired'
                    ? ProRequiredModalProps
                    : T extends 'channelLead'
                      ? ChannelLeadConfig
                      : T extends 'loginPrompt'
                        ? LoginPromptConfig
                        : T extends 'notification'
                          ? NotificationModalParams
                          : T extends 'schoolSuggestions'
                            ? SchoolSuggestionModalProps
                            : T extends 'registerOrLogin'
                              ? RegisterOrLoginModalConfig
                              : never;

type ModalResponse<T extends AllModals> = T extends 'locationPicker'
  ? LocationPickerModalProviderResponse
  : T extends 'promptString'
    ? PromptStringModalResponse
    : T extends 'loginPrompt'
      ? LoginPromptModalResult
      : T extends 'proRequired'
        ? ProRequiredModalResponse
        : T extends 'channelLead'
          ? ChannelLeadResponse
          : T extends 'requestFollowChannel'
            ? FollowChannelModalResponse
            : T extends 'notification'
              ? NotificationModalResponse
              : T extends 'schoolSuggestions'
                ? SchoolSuggestionModalResponse
                : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-generated TECH-258
                  any;

export class SearchAppModalManagerService extends AppModalManagerService {
  public open<T extends ModalsWithConfig, O extends ModalOptions<T>>(modal: T, options: O): Promise<ModalResponse<T>>;
  // open<T extends ModalsWithOptionalConfig, O extends ModalOptions<T>>(modal: T, options?: O): Promise<any>;
  public open<T extends ModalsWithoutConfig>(modal: T): Promise<ModalResponse<T>>;
  public open<T extends AllModals>(modalIn: T, options?: ModalOptions<T>): Promise<ModalResponse<T>> {
    // why typescript?
    const modal: AllModals = modalIn;
    type Resp = ModalResponse<T>;

    return this.setModal<Resp>((closeFn: (result) => undefined) => {
      const defaultModalProps = {
        closeModal: closeFn,
        key: modal,
      };

      const { key, ...otherDefaultModalProps } = defaultModalProps;

      switch (modal) {
        case 'login':
          return <LoginModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />;
        case 'lessonSynced':
          return <LessonsSyncedModal key={key} {...otherDefaultModalProps} />;
        case 'confirm':
          return <ConfirmModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />;
        case 'locationPicker':
          return (
            <LocationPickerModalProvider
              key={key}
              {...(options as ModalOptions<typeof modal>)}
              {...otherDefaultModalProps}
            />
          );
        case 'promptString':
          return (
            <PromptStringModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'shareLesson':
          return (
            <ShareLessonModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'flagLesson':
          return <FlagLessonModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />;
        case 'requestFollowChannel':
          return (
            <FollowChannelModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'forgotPassword':
          return <ForgotPasswordModal key={key} {...otherDefaultModalProps} />;
        case 'link':
          return <LinkModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />;
        case 'lockedFolder':
          return (
            <LockedFolderModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'loginPrompt':
          return (
            <LoginPromptModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'proRequired':
          return (
            <ProRequiredModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'channelLead':
          return (
            <ChannelLeadModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'cookiePreferences':
          return (
            <CookiePopup
              show={tracker.requiresCookieConsent()}
              onAccept={() => tracker.setCookieSettings({ analytic: true, marketing: true })}
            />
          );
        case 'notification':
          return (
            <NotificationModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'schoolSuggestions':
          return (
            <SchoolSuggestion key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        case 'registerOrLogin':
          return (
            <RegisterOrLoginModal key={key} {...(options as ModalOptions<typeof modal>)} {...otherDefaultModalProps} />
          );
        default:
          assertNever(modal, 'SearchAppModalManagerService');
      }
    });
  }
}

export const searchAppModalManagerService = new SearchAppModalManagerService();
